// src/hooks/useGameLogic.js
import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { shuffleArray } from "../utils/helpers";
import useFeedbackPrompt from './useFeedbackPrompt';

const GAME_OVER_POPUP_DURATION = 5000;
const QUIZ_LENGTH = 10;

const useGameLogic = ({
    activityType,
    initialLives = 5,
    gameSpecificEndCondition = false,
    currentScore = 0,
    feedbackConfig,
    // map incoming `navigate` to `_navigate` to avoid unused-parameter warnings
    navigate: _navigate,
    gameLength = QUIZ_LENGTH,
    inJourney = false, // Journey mode flag
    onComplete = null, // Journey completion callback
}) => {
    const { user } = useAuth();
    const backendApiUrl = process.env.REACT_APP_BACKEND_API_URL;

    const [lives, setLives] = useState(initialLives);
    const [streak, setStreak] = useState(0);
    const [negativeStreak, setNegativeStreak] = useState(0);
    const [showStreakPopup, setShowStreakPopup] = useState(false);
    const [streakMessage, setStreakMessage] = useState('');
    const [showEncouragementPopup, setShowEncouragementPopup] = useState(false);
    const [encouragementMessage, setEncouragementMessage] = useState('');
    const [showGameOverPopup, setShowGameOverPopup] = useState(false);
    const [isGameEnded, setIsGameEnded] = useState(false);
    // Removed local feedback modal state - use centralized hook instead
    const [redirectMessage, setRedirectMessage] = useState("");
    const [questions, setQuestions] = useState([]);
    const [current, setCurrent] = useState(0);
    const [loadingQuestions, setLoadingQuestions] = useState(false);
    const [error, setError] = useState(null);
    const [flashcardsToReview, setFlashcardsToReview] = useState([]);
    const [showInstructions, setShowInstructions] = useState(false);

    const encouragementPopupTimerRef = useRef(null);
    const streakPopupTimerRef = useRef(null);
    const gameOverPopupTimerRef = useRef(null);
    const feedbackModalTimerRef = useRef(null);
    const redirectTimerRef = useRef(null);

    const clearAllTimers = useCallback(() => {
        clearTimeout(encouragementPopupTimerRef.current);
        clearTimeout(streakPopupTimerRef.current);
        clearTimeout(gameOverPopupTimerRef.current);
        clearTimeout(feedbackModalTimerRef.current);
        clearTimeout(redirectTimerRef.current);
    }, []);

    // Centralized feedback prompt hook
    const feedbackPrompt = useFeedbackPrompt({
        activityKey: activityType || 'activity',
        isJourney: inJourney,
        navigate: _navigate,
        promptInterval: feedbackConfig?.PROMPT_INTERVAL || 1,
        dismissDays: feedbackConfig?.DISMISS_DAYS || 7,
    });

    const submitScoreToLeaderboard = useCallback(async (finalScore) => {
        if (inJourney) return;

        if (user && user.uid === "guest-user") {
            setRedirectMessage("Thanks for playing! Redirecting to home...");
            return;
        }

        if (!user || !user.uid || !user.username) {
            setRedirectMessage("User data missing for score submission. Please log in properly.");
            return;
        }

        setRedirectMessage("Submitting score...");
        const cleanedUrl = backendApiUrl.endsWith('/') ? backendApiUrl.slice(0, -1) : backendApiUrl;
        const apiUrl = `${cleanedUrl}/scores`;

        try {
            await axios.post(apiUrl, {
                userId: user.uid,
                username: user.username,
                score: finalScore,
                activityType,
                avatar: user.avatar || user.photoURL || '/default-avatar.png',
            });
            setRedirectMessage("Score submitted! Redirecting to home...");
        } catch (error) {
            console.error('Failed to submit score:', error.response ? error.response.data : error.message);
            setRedirectMessage("Failed to submit score. Redirecting to home...");
        }
    }, [user, backendApiUrl, activityType, inJourney]);

    // We now delegate feedback prompting to feedbackPrompt.showIfAppropriate

    // Update user progress for a question (used by correct/incorrect handlers)
    const updateUserProgress = useCallback(async (questionId, isCorrect) => {
        if (!user || !user.uid || user.uid === "guest-user") return;

        try {
            const cleanedUrl = backendApiUrl.endsWith('/') ? backendApiUrl.slice(0, -1) : backendApiUrl;
            const apiUrl = `${cleanedUrl}/user-progress`;
            await axios.post(apiUrl, {
                userId: user.uid,
                activityType,
                questionId,
                answeredCorrectly: isCorrect,
            });
        } catch (err) {
            console.error("Failed to update user progress:", err.response?.data || err.message);
        }
    }, [user, activityType, backendApiUrl]);

    // --- Core Fix: Handle end of game and Journey progression ---
    useEffect(() => {
        if (questions.length === 0) return;

        const gameLost = lives <= 0;
        const gameCompleted = gameSpecificEndCondition || (current >= questions.length && flashcardsToReview.length === 0);
        const endConditionMet = gameLost || gameCompleted;

        if (endConditionMet && !isGameEnded) {
            if (gameLost) setLives(0); // Cap lives at 0 on loss

            // 🟢 CRITICAL FIX: Prioritize Journey completion and skip popups
            if (inJourney) {
                if (typeof onComplete === "function") {
                    onComplete(activityType);
                }
            } else {
                // Standalone Mode Logic
                if (gameLost) {
                    // Only show Game Over popup if lives ran out in standalone mode
                    setShowGameOverPopup(true);
                }
                // No need to set popup on completion (win), as the component's
                // render logic already handles the final score screen via the 'done' state.
            }

            // Execute final actions once for either mode
            setIsGameEnded(true);
            submitScoreToLeaderboard(currentScore);
        }
    }, [
        lives,
        gameSpecificEndCondition,
        isGameEnded,
        currentScore,
        submitScoreToLeaderboard,
        current,
        questions.length,
        flashcardsToReview.length,
        inJourney,
        onComplete,
        activityType,
    ]);

    useEffect(() => {
        if (activityType === 'flashcards' && current >= questions.length && flashcardsToReview.length > 0) {
            setQuestions(flashcardsToReview);
            setFlashcardsToReview([]);
            setCurrent(0);
        }
    }, [activityType, current, questions.length, flashcardsToReview]);

    // Skip auto-redirect in Journey mode
    useEffect(() => {
        // When a standalone game ends, show the feedback modal (no auto-redirect).
        if (isGameEnded && !inJourney) {
            // Add a brief delay so the view's final screen (result UI) can render
            // and any component-level state settles before the modal overlays it.
            feedbackModalTimerRef.current = setTimeout(() => {
                feedbackPrompt.showIfAppropriate();
            }, 250);
        }
        // No timers or automatic navigation here — navigation should remain manual.
        return () => clearTimeout(feedbackModalTimerRef.current);
    }, [isGameEnded, inJourney, feedbackPrompt]);

    // --- Core Fix: Fetch Questions ---
    const fetchQuestionsFromBackend = useCallback(async (currentSurahId = null) => {
        const cleanedUrl = backendApiUrl.endsWith('/') ? backendApiUrl.slice(0, -1) : backendApiUrl;
        let apiUrl;

        // ✅ Correct endpoint routing based on activity type
        if (activityType === 'flashcards' && currentSurahId) {
            apiUrl = `${cleanedUrl}/flashcards/surah/${currentSurahId}`;
        } else if (activityType === 'originalQuiz' || activityType === 'quiz') {
            apiUrl = `${cleanedUrl}/quiz`;
        } else if (activityType === 'fillInTheBlanks') {
            apiUrl = `${cleanedUrl}/ayahblanks`; // ✅ Note: ayahblanks not ayah-blanks
        } else if (activityType === 'halalvsharam') {
            apiUrl = `${cleanedUrl}/halalvsharam`;
        } else {
            // Default fallback
            apiUrl = `${cleanedUrl}/${activityType}`;
        }

        console.log(`📡 Fetching from: ${apiUrl}`);
        const res = await axios.get(apiUrl);
        return res.data;
    }, [backendApiUrl, activityType]);

    const loadQuizQuestions = useCallback(async (surahIdOrPreloadedData = null, doShuffle = true) => {
        setLoadingQuestions(true);
        setError(null);
        setCurrent(0);
        setQuestions([]);
        setFlashcardsToReview([]);

        if (!user) {
            setLoadingQuestions(false);
            setError("Please login to access the game.");
            return;
        }

        // ✅ Special check for flashcards - they MUST have a surah ID
        if (activityType === 'flashcards' && !surahIdOrPreloadedData) {
            setLoadingQuestions(false);
            setError("Please select a Surah to begin.");
            return;
        }

        try {
            let allQuestions;

            // ✅ Check if preloaded data was passed directly (for HalalVSHaram, etc.)
            if (Array.isArray(surahIdOrPreloadedData)) {
                console.log("✅ Using preloaded data array directly");
                allQuestions = surahIdOrPreloadedData;
            } else {
                // ❌ Fetch from backend (for flashcards with surahId, or if no preload)
                console.log("⚠️ Fetching from backend...");
                allQuestions = await fetchQuestionsFromBackend(surahIdOrPreloadedData);
            }

            // ✅ CRITICAL: Ensure allQuestions is an array
            if (!Array.isArray(allQuestions)) {
                console.error("❌ Expected array but got:", typeof allQuestions, allQuestions);
                setError("Invalid data format received from server.");
                setLoadingQuestions(false);
                return;
            }

            if (allQuestions.length === 0) {
                setError("No questions available for this activity.");
                setLoadingQuestions(false);
                return;
            }

            const userProgress = (user && user.uid !== "guest-user")
                ? (await axios.get(`${backendApiUrl}/user-progress?activityType=${activityType}`, {
                    headers: { 'X-User-Id': user.uid }
                })).data
                : {};

            let questionsToUse = [...allQuestions];

            if (doShuffle) {
                let selectedQuestions = [];

                if (user && user.uid !== "guest-user") {
                    const incorrectlyAnsweredQ = allQuestions.filter(q => userProgress[q._id]?.answeredCorrectly === false);
                    const unansweredQ = allQuestions.filter(q => !userProgress[q._id]);
                    selectedQuestions.push(...shuffleArray(incorrectlyAnsweredQ).slice(0, gameLength));
                    if (selectedQuestions.length < gameLength) {
                        const remainingSlots = gameLength - selectedQuestions.length;
                        selectedQuestions.push(...shuffleArray(unansweredQ).slice(0, remainingSlots));
                    }
                }

                if (selectedQuestions.length < gameLength) {
                    const remainingSlots = gameLength - selectedQuestions.length;
                    const currentSelectedIds = new Set(selectedQuestions.map(q => q._id));
                    const fillFromAll = shuffleArray(allQuestions)
                        .filter(q => !currentSelectedIds.has(q._id))
                        .slice(0, remainingSlots);
                    selectedQuestions.push(...fillFromAll);
                }

                questionsToUse = selectedQuestions;
            }

            const questionsWithShuffledOptions = questionsToUse.map(q => {
                let correctAnswerValue = null;
                let mappedOptions = [];

                if (q.blanks && Array.isArray(q.blanks)) {
                    mappedOptions = shuffleArray(q.options);
                    correctAnswerValue = q.blanks;
                } else if (Array.isArray(q.options) && q.options.length > 0) {
                    const correctOption = q.options.find(option => option.isCorrect || option.iscorrect);
                    if (correctOption) {
                        correctAnswerValue = correctOption.text;
                        mappedOptions = shuffleArray(q.options.map(option => option.text));
                    } else if (typeof q.options[0] === 'string') {
                        mappedOptions = shuffleArray([...q.options]);
                        correctAnswerValue = q.correctMeaning || q.correctAnswerText || q.answer ||
                            (typeof q.correctAnswer === 'number' ? q.options[q.correctAnswer] : null);
                    }
                }

                if (!correctAnswerValue) {
                    correctAnswerValue = q.correctAnswerText || q.answer ||
                        q.options?.[q.correctAnswer] || q.options?.[0] || 'Unknown';
                }
                if (mappedOptions.length === 0 && q.options) {
                    mappedOptions = q.options.map(option =>
                        typeof option === 'string' ? option : option.text
                    );
                }

                return {
                    ...q,
                    options: mappedOptions,
                    correctAnswerText: correctAnswerValue,
                    correctMeaning: q.meaning
                };
            });

            setQuestions(questionsWithShuffledOptions);
        } catch (err) {
            console.error("❌ Error loading quiz questions:", err);
            setError(err.message || "Failed to load quiz. Please try again.");
        } finally {
            setLoadingQuestions(false);
        }
    }, [fetchQuestionsFromBackend, gameLength, user, activityType, backendApiUrl]);

    const handleCorrectAttempt = useCallback((questionId) => {
        setStreak(prev => prev + 1);
        setNegativeStreak(0);
        if (questionId) updateUserProgress(questionId, true);
    }, [updateUserProgress]);

    const handleIncorrectAttempt = useCallback((questionId) => {
        setLives(prev => Math.max(0, prev - 1));
        setNegativeStreak(prev => prev + 1);
        setStreak(0);
        if (questionId) updateUserProgress(questionId, false);
    }, [updateUserProgress]);

    const handleFlashcardAction = useCallback((wasMastered) => {
        const currentCard = questions[current];
        if (!currentCard) return;

        if (wasMastered) updateUserProgress(currentCard._id, true);
        else setFlashcardsToReview(prev => [...prev, currentCard]) && updateUserProgress(currentCard._id, false);

        setCurrent(prev => prev + 1);
    }, [questions, current, updateUserProgress]);

    const resetGameLogicStates = useCallback(() => {
        setLives(initialLives);
        setStreak(0);
        setNegativeStreak(0);
        setShowStreakPopup(false);
        setStreakMessage('');
        setShowEncouragementPopup(false);
        setEncouragementMessage('');
        setShowGameOverPopup(false);
        setIsGameEnded(false);
        setRedirectMessage("");
        setQuestions([]);
        setCurrent(0);
        setLoadingQuestions(true);
        setError(null);
        clearAllTimers();
        setFlashcardsToReview([]);
    }, [initialLives, clearAllTimers]);

    // Reduce popup frequency in Journey mode
    useEffect(() => {
        if (streak > 0 && streak % 3 === 0 && !inJourney) {
            if (streakPopupTimerRef.current) clearTimeout(streakPopupTimerRef.current);
            setShowStreakPopup(true);
            setStreakMessage(`${streak} in a row, Masha'Allah!`);
            streakPopupTimerRef.current = setTimeout(() => {
                setShowStreakPopup(false);
                setStreakMessage('');
            }, 2500);
        }
    }, [streak, inJourney]);

    useEffect(() => {
        if (negativeStreak > 0 && negativeStreak % 2 === 0 && !inJourney) {
            if (encouragementPopupTimerRef.current) clearTimeout(encouragementPopupTimerRef.current);
            const ENCOURAGEMENT_MESSAGES = ["Keep going!", "Don't give up!"];
            const messageIndex = ((negativeStreak / 2) - 1) % ENCOURAGEMENT_MESSAGES.length;
            setEncouragementMessage(ENCOURAGEMENT_MESSAGES[messageIndex]);
            setShowEncouragementPopup(true);

            encouragementPopupTimerRef.current = setTimeout(() => {
                setShowEncouragementPopup(false);
                setEncouragementMessage('');
            }, 2500);
        }
    }, [negativeStreak, inJourney]);

    useEffect(() => {
        if (showGameOverPopup && !inJourney) {
            gameOverPopupTimerRef.current = setTimeout(() => feedbackPrompt.showIfAppropriate(), GAME_OVER_POPUP_DURATION);
        }
        return () => clearTimeout(gameOverPopupTimerRef.current);
    }, [showGameOverPopup, feedbackPrompt, inJourney]);

    // bookkeeping for games played is handled inside feedbackPrompt.showIfAppropriate

    return {
        lives,
        showGameOverPopup,
        showStreakPopup,
        streakMessage,
        showEncouragementPopup,
        encouragementMessage,
        showFeedbackModal: feedbackPrompt.showFeedbackModal,
        handleCloseFeedbackModal: feedbackPrompt.handleCloseFeedbackModal,
        handleDismissFeedbackPermanently: feedbackPrompt.handleDismissFeedbackPermanently,
        handleCorrectAttempt,
        handleIncorrectAttempt,
        handleFlashcardAction,
        resetGameLogicStates,
        redirectMessage,
        questions,
        current,
        setCurrent,
        loadingQuestions,
        error,
        loadQuizQuestions,
        showInstructions,
        setShowInstructions,
        isGameEnded,
    };
};

export default useGameLogic;

